"use client"

import { useState } from "react"
import Header from "../../components/Header/Header"
import Footer from "../../components/Footer/Footer"
import styles from "./FAQPage.module.css"

const faqsByCategory = {
  'Travel & Accommodation': [
    "What is the first step to begin my journey from abroad?",
    "What are my options for accommodation during my stay?",
    "Do you provide language interpretation services?",
    "When should I plan to arrive before my treatment?",
    "Is there assistance available for visa and travel formalities?",
    "How are airport transfers and local transportation handled?",
    "What pre-travel medical documents or tests are required?",
    "Is it possible to schedule multiple treatments during a single visit?",
  ],
  'Your Treatment Journey': [
    "What can I expect from my initial in-person consultation and evaluation?",
    "What is the typical timeline for the day of my procedure?",
    "What type of anesthesia or sedation will be used for my comfort and safety?",
    "What are your specific protocols and standards to ensure patient safety and consistent outcomes?",
    "Is it possible to preview the expected results of my treatment digitally before we begin?",
    "What is your policy on rescheduling appointments if my travel plans change?",
    "What measures are in place to ensure my comfort throughout an extended procedure?",
    "Are companions allowed to accompany me during any parts of my visit?",
  ],
  'Recovery & Aftercare': [
    "What is the recommended post-operative stay for my recovery and follow-up checks?",
    "What pre-departure guidance and support is provided to ensure I am ready for my journey home?",
    "How is my long-term recovery monitored and supported after I return to my home country?",
    "What is the protocol for urgent medical or non-medical inquiries after my procedure?",
    "What are the essential post-operative guidelines regarding diet, lifestyle, and physical activities?",
    "Do you assist in coordinating local aftercare services, such as rehabilitation or maintenance treatments, in my home country?",
    "How is the long-term follow-up and final results assessment scheduled and conducted?",
    "What is included in your commitment to patient care and satisfaction following the procedure?"
  ],
}

const answersByCategory = {
  'Travel & Accommodation': {
    0: "Getting started is simple. Just share your details and treatment goals through our online form, and a dedicated medical advisor will personally contact you to outline your personalized journey.",
    1: "To make your stay as convenient as possible, we have pre-arranged preferred partnerships with a selection of hotels near our clinic, catering to a range of budgets. We are happy to share these options and manage the booking process on your behalf.",
    2: "To ensure we understand your needs perfectly, our professional interpretation team supports a wide range of languages, including English, French, Italian, Russian, Arabic, and German. This service is available throughout your entire journey, from initial inquiry to post-operative care.",
    3: "For an optimal preparation, we advise planning your arrival at least one full day prior to your procedure. This allows ample time for your mandatory pre-operative consultation and to acclimatize and rest before surgery.",
    4: "Definitely. To facilitate your travel, we will furnish you with a formal invitation letter and all necessary medical referrals required for your medical visa application or expedited border crossing.",
    5: "Your journey begins and ends with care. We provide complimentary private chauffeur services to and from the airport for your absolute comfort and convenience.",
    6: "Essential documents include your passport, our provided invitation letter, and travel insurance proof. For ongoing medical conditions, please bring recent related reports (e.g., for diabetes or cardiac issues). Importantly, there's no need to organize scans or tests beforehand; we will conduct all required diagnostic imaging and laboratory work upon your arrival.",
    7: "Yes, combining treatments is a common and efficient choice. Our treatment coordinators will meticulously plan a consolidated schedule for your procedures (such as hair, dental, and aesthetic), prioritizing both your safety with adequate recovery time between operations and the efficiency of your visit.",
  },
  'Your Treatment Journey': {
    0: "Your initial consultation is a comprehensive assessment. Our lead surgeon will conduct an in-depth evaluation, which includes a detailed medical history review, a physical examination, and necessary imaging. This process allows us to collaborate with you in finalizing a treatment plan that is precisely tailored to your anatomy and goals.",
    1: "Your comfort and safety are paramount in our anesthesia approach. The type used is carefully selected based on your procedure's complexity and duration, ranging from local anesthesia for minor treatments to monitored IV sedation or general anesthesia for more extensive surgeries, always administered by a specialist.",
    2: "Absolutely. We leverage advanced digital planning tools, such as Digital Smile Design and 3D imaging, to create a preview of your expected results. This allows us to align on a clear vision and ensures you are fully confident in your personalized treatment plan before we begin.",
    3: "We proactively manage your comfort throughout longer procedures with scheduled rest periods, refreshments, and constant monitoring of your well-being by our clinical team. Our dedicated team is trained to ensure you feel physically at ease and fully informed at every stage.",
    4: "Procedure times are tailored to the specific treatment plan. As a general guide, you can anticipate hair and aesthetic sessions to take approximately 6-8 hours, while most dental surgeries are typically completed within 2-4 hours. Your surgeon will provide you with a precise timeline during your consultation.",
    5: "Our commitment to safety is unwavering. It is built on a foundation of internationally accredited surgical facilities, state-of-the-art technology, and the exclusive use of certified, high-grade materials. Every protocol is designed to meet the highest global standards of patient care.",
    6: "We recognize that international travel requires flexibility. With at least two weeks' notice, we are happy to accommodate changes to your schedule at no extra charge, ensuring your journey remains as stress-free as possible.",
    7: "We encourage you to bring a companion for support. They are welcome to wait in our dedicated guest lounge with complimentary amenities and are invited to join you for the post-operative briefing to help understand your aftercare instructions.",
  },
  'Recovery & Aftercare': {
    0: "Your recommended stay is tailored to your specific procedure to ensure a safe and successful initial recovery. While hair transplant and dental implant patients typically require 2-3 nights for essential post-operative care, more complex procedures like plastic surgery or full smile design often benefit from a longer stay of approximately one week. This allows our team to provide close monitoring, manage your comfort, and perform any necessary adjustments before you depart.",
    1: "Your recovery journey is continuously supported through our structured virtual follow-up program. We schedule regular check-ins via telemedicine at key milestones (e.g., 1 week and 1 month post-procedure), and you are always welcome to send progress photos or request a video consultation whenever you have questions.",
    2: "You will receive a personalized, easy-to-follow aftercare plan before you depart. This comprehensive guide provides clear, day-by-day instructions on diet, activity restrictions, and skincare tailored specifically to your procedure, ensuring you know exactly how to support your healing process at every stage.",
    3: "To ensure your complete satisfaction, we schedule a comprehensive results evaluation at the 6 to 12-month mark, once healing is fully complete. This final review, conducted either in-person or via video call, allows us to formally assess the outcome and discuss any potential touch-ups to perfect your results.",
    4: "Upon discharge, you will receive a comprehensive recovery kit. This includes any necessary medications, detailed care instructions, clear emergency contact information, and a personalized schedule for your remote follow-up appointments, ensuring you feel fully prepared and supported for your journey home.",
    5: "For uninterrupted support, you will have direct access to our 24/7 patient hotline and a dedicated messaging line. These channels connect you immediately with your care coordinator and our nursing team for any urgent or non-urgent questions, day or night.",
    6: "o provide a seamless continuum of care, we can seamlessly arrange any recommended adjunct therapies—such as lymphatic drainage massage, physiotherapy, or aesthetic touch-ups—with our trusted network of in-house or partner specialists.",
    7: "Our commitment to your results is backed by clear warranties. This includes written coverage on dental implants and specific graft survival guarantees for hair procedures, with details provided in your treatment plan. We firmly stand by the quality of our work and will address any concerns in accordance with our policy, which may include complimentary touch-up sessions where medically indicated.",
  },
}

// populate default placeholder answers for categories that don't have custom answers yet
Object.keys(faqsByCategory).forEach((cat) => {
  if (!answersByCategory[cat] || Object.keys(answersByCategory[cat]).length === 0) {
    answersByCategory[cat] = {}
    faqsByCategory[cat].forEach((q, i) => {
      answersByCategory[cat][i] = `Answer to: ${q}`
    })
  }
})

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null)
  const [selectOpen, setSelectOpen] = useState(false)
  const [selected, setSelected] = useState('Travel & Accommodation')

  // when category changes, reset opened accordion
  const changeCategory = (cat) => {
    setSelected(cat)
    setSelectOpen(false)
    setOpenIndex(null)
  }

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i)

  return (
    <>
      <Header />

      <main className={styles.section}>
        <div className={styles.container}>
          <h1 className={styles.pageTitle}>F.A.Q</h1>

          <div className={styles.greenStrip}>
            <div className={styles.stripLeft}>We're Here to Help. Explore Our FAQs or Contact Us Directly.</div>
            <div className={styles.stripRight}>
              <div className={styles.selectWrap}>
                <button className={styles.selectBtn} onClick={() => setSelectOpen((s) => !s)}>{selected} ▾</button>
                {selectOpen && (
                  <div className={styles.selectMenu} role="menu">
                    {Object.keys(faqsByCategory).map((opt) => (
                      <div key={opt} className={styles.selectItem} role="menuitem" onClick={() => changeCategory(opt)}>
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.contentGrid}>
            <div>
              <div className={styles.accordion}>
                {faqsByCategory[selected].slice(0,4).map((q, idx) => (
                  <div key={idx} className={styles.faqItem}>
                    <button className={`${styles.faqButton} ${openIndex === idx ? 'active' : ''}`} onClick={() => toggle(idx)}>
                      <span className={styles.question}>{q}</span>
                      <span className={styles.plusIcon}>{openIndex === idx ? '−' : '+'}</span>
                    </button>
                    {openIndex === idx && (
                      <div className={styles.answer}><p>{answersByCategory[selected][idx]}</p></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className={styles.accordion}>
                {faqsByCategory[selected].slice(4).map((q, idx) => {
                  const realIdx = idx + 4
                  return (
                    <div key={realIdx} className={styles.faqItem}>
                      <button className={`${styles.faqButton} ${openIndex === realIdx ? 'active' : ''}`} onClick={() => toggle(realIdx)}>
                        <span className={styles.question}>{q}</span>
                        <span className={styles.plusIcon}>{openIndex === realIdx ? '−' : '+'}</span>
                      </button>
                      {openIndex === realIdx && (
                        <div className={styles.answer}><p>{answersByCategory[selected][realIdx]}</p></div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}

